import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, LogIn, Zap, Rocket } from "lucide-react";
import { authService } from '../../services/authService';
import './SignIn.css';

const SignIn = ({ onLogin }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = t('validation.emailRequired', 'Email is required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('validation.emailInvalid', 'Invalid email format');
    }

    if (!formData.password) {
      newErrors.password = t('validation.passwordRequired', 'Password is required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await authService.signIn(formData.email, formData.password);
      onLogin(response.user, response.token);
    } catch (error) {
      setErrors({ general: error.message || t('auth.signInError', 'Invalid email or password') });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          {/* Header */}
          <div className="auth-header">
            <div className="auth-logo">
              <Zap size={28} />
            </div>
            <h1 className="auth-title">{t('auth.login', 'Login')}</h1>
            <p className="auth-subtitle">{t('auth.welcomeBack', 'Welcome back to Matrix')}</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="auth-form">
            {errors.general && (
              <div className="error-alert">
                {errors.general}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email" className="form-label">
                {t('auth.email', 'Email')}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t('auth.email', 'Email')}
                autoComplete="email"
                className={`form-input ${errors.email ? 'error' : ''}`}
              />
              {errors.email && <div className="error-message">{errors.email}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">
                {t('auth.password', 'Password')}
              </label>
              <div className="input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={t('auth.password', 'Password')}
                  autoComplete="current-password"
                  className={`form-input form-input-password ${errors.password ? 'error' : ''}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="password-toggle"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && <div className="error-message">{errors.password}</div>}
            </div>

            {/* Remember me + Forgot password (stacked, centered) */}
            <div className="auth-options-stack">
              <label className="remember-me">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span>{t('auth.rememberMe', 'Remember me')}</span>
              </label>
              <Link to="/forgot-password" className="forgot-password-link">
                {t('auth.forgotPassword', 'Forgot password?')}
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
            >
              {loading ? (
                <>
                  <span className="spinner-small"></span>
                  {t('common.loading', 'Loading...')}
                </>
              ) : (
                <>
                  <LogIn size={18} />
                  {t('auth.signIn', 'Sign in')}
                </>
              )}
            </button>
          </form>

          {/* Self Assessment Link */}
          <div className="self-assessment-link">
            <Link to="/self-assessment/signup" className="btn-self-assessment">
              <Rocket size={16} />
              <span>{t('auth.tryDemo', 'Try Self Assessment')}</span>
            </Link>
          </div>

          {/* Footer */}
          <div className="auth-footer">
            <p className="auth-footer-text">
              {t('auth.noAccount', "Don't have an account?")}{' '}
              <Link to="/signup" className="auth-link">
                {t('auth.signUp', 'Sign up')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
